import type { SanityImageSource } from '@sanity/image-url';
import { createImageUrlBuilder } from '@sanity/image-url';
import { dataset, projectId } from '@/lib/sanity/core/config';
import type { BlockImageDto, MediaDto } from '@/lib/sanity/dto';

// Plain project coordinates, not the `client` instance: Client Components import
// `sanityImage`, and passing the client would ship `createClient` with them.
const builder = createImageUrlBuilder({ projectId, dataset });

/** Quality of URLs the browser downloads as they are. */
const IMAGE_QUALITY = 90;

/**
 * Quality of the CDN source next/image re-encodes. Those URLs are an intermediate
 * the optimizer fetches server-side once, never a file the browser downloads, so
 * they stay near-lossless: compressing at 80 here and again in the optimizer costs
 * quality twice for the same delivered bytes.
 */
const OPTIMIZER_SOURCE_QUALITY = 90;

export function urlForImage(source: SanityImageSource) {
    return builder.image(source);
}

export function sanityImageSection(img?: MediaDto, maxWidth = 1920) {
    return img?.asset
        ? builder.image(img).width(maxWidth).fit('max').auto('format').quality(IMAGE_QUALITY).url()
        : undefined;
}

/**
 * Intrinsic pixel size encoded in a Sanity asset `_ref` (`image-…-{w}x{h}-{ext}`).
 * Lets a non-`fill` <Image> reserve the correct aspect ratio without a network
 * round-trip, so a projected block image can render at its natural proportions.
 */
export function sanityImageDimensions(ref?: string): { width: number; height: number } | undefined {
    const match = ref?.match(/-(\d+)x(\d+)-\w+$/);
    return match ? { width: Number(match[1]), height: Number(match[2]) } : undefined;
}

export const sanityImage = {
    og: (img?: MediaDto) =>
        img?.asset ? builder.image(img).width(1200).height(630).fit('max').quality(IMAGE_QUALITY).url() : undefined,
    hero: (img?: BlockImageDto) =>
        img?.ref
            ? builder.image(img.ref).width(2600).fit('max').auto('format').quality(OPTIMIZER_SOURCE_QUALITY).url()
            : undefined,
    pillar: (img?: BlockImageDto) =>
        img?.ref
            ? builder.image(img.ref).width(1600).fit('max').auto('format').quality(OPTIMIZER_SOURCE_QUALITY).url()
            : undefined,
    showcase: (img?: BlockImageDto) =>
        img?.ref
            ? builder.image(img.ref).width(1200).fit('max').auto('format').quality(OPTIMIZER_SOURCE_QUALITY).url()
            : undefined,
    roadmap: (img?: BlockImageDto) =>
        img?.ref
            ? builder
                  .image(img.ref)
                  .width(600)
                  .height(338)
                  .fit('crop')
                  .auto('format')
                  .quality(OPTIMIZER_SOURCE_QUALITY)
                  .url()
            : undefined,
};

export type SanityImageVariant = keyof typeof sanityImage;
